<template>
  <div id="categories" class="mb-3">
    <div class="publish-buttons row mt-5 d-flex flex-column justify-center">
      <v-btn
        class="draft-btn m-1"
        small
        color="grey lighten-3"
        @click="saveArticle"
      >
        <v-icon>save</v-icon>
        <span v-if="isDraft">{{ $store.state.user.langEng ? 'Save draft' : ' Spara utkast' }}</span>
        <span v-else>{{ $store.state.user.langEng ? 'Save' : 'Spara' }}</span>
      </v-btn>

      <v-btn
        v-if="
          !article.deleted &&
            article._id &&
            article.klasses.length > 1 &&
            article.klasses
              .map((k) => k.slug)
              .includes($store.getters.classNameInUrl)
        "
        class="publish-btn m-1"
        small
        color="grey lighten-1"
        @click="detachArticle"
      >
        <v-icon>delete</v-icon>
        {{ ($store.state.user.langEng ? 'Delete from ' : 'Radera från ') + $store.getters.classNameInUrl}}
      </v-btn>
      <v-btn
        v-else-if="!article.deleted && article._id"
        class="publish-btn m-1"
        small
        color="grey lighten-1"
        @click="displayDeleteArticleDialog = true"
      >
        <v-icon>delete</v-icon>
        {{ $store.state.user.langEng ? 'Delete' : 'Radera'}}
      </v-btn>
      <v-btn
        v-if="!isPublished || !article._id"
        class="publish-btn m-1"
        small
        color="grey lighten-1"
        @click="publishArticle"
      >
        <v-icon>publish</v-icon>
        {{ $store.state.user.langEng ? 'Publish' : 'Publicera' }}
      </v-btn>
      <v-btn
        v-if="isPublished && article._id"
        class="draft-btn m-1"
        small
        color="grey lighten-1"
        @click="displayUnpublishDialog = true"
      >
        <v-icon>visibility_off</v-icon>
        {{ $store.state.user.langEng ? 'Unpublish' : 'Avpublicera' }}
      </v-btn>
    </div>
    <div v-if="article.klasses.length" class="text-left">
      <div>{{ $store.state.user.langEng ? 'Classes using article:' : 'Klasser som använder artikeln:' }}</div>
      <v-chip
        v-for="(klass, i) in article.klasses"
        :key="klass + i"
        class="chip-underline p-2"
        :text-color="klassColor(i) + ' lighten-2'"
        color="transparent"
        dark
        small
        >{{ klass.slug }}</v-chip
      >
    </div>
    <v-text-field
      class="mt-2 mb-3"
      type="text"
      v-model="filter"
      :append-icon="filter ? 'close' : ''"
      @click:append="filter = ''"
      @keydown.esc="filter = ''"
      :placeholder="$store.state.user.langEng ? 'Search category' : 'Sök kategori'"
      autocomplete="off"
      solo
    ></v-text-field>
    <v-card
      id="filtered-categories"
      class="p-0"
      :class="{ 'error-outline': categoryError }"
    >
      <v-btn
        class="add-new-category-button"
        @click="toggleNewCategory = !toggleNewCategory"
        text
        block
      >{{$store.state.user.langEng ? 'Add new...' : 'Lägg till...'}}</v-btn>
      <transition
        name="animate-route"
        mode="out-in"
        enter-active-class="animated flipInX"
        leave-active-class="animated flipOutX"
      >
        <v-text-field
          v-show="toggleNewCategory"
          class="new-category-input"
          v-model="newCategory"
          append-icon="add"
          @click:append="addNewCategory"
          @keyup.enter="addNewCategory"
          color="grey"
          :placeholder="$store.state.user.langEng ? 'New category' : 'Ny kategori'"
          autocomplete="off"
        ></v-text-field>
      </transition>
      <transition-group
        name="animate-route"
        mode="out-in"
        enter-active-class="animated fadeIn"
        leave-active-class="animated fadeOut"
      >
        <v-checkbox
          v-for="category in categories"
          :id="'create-article-' + category.name"
          :key="category.name"
          v-model="selectedCategories"
          :label="category.name"
          :value="category"
          color="grey"
          @change="emitSelectedCategories"
        ></v-checkbox>
      </transition-group>
    </v-card>
    <v-dialog v-model="displayUnpublishDialog" max-width="400px">
      <v-card>
        <v-card-title>{{ $store.state.user.langEng ? 'Are you sure?' : 'Är du säker?' }}</v-card-title>
        <v-card-actions class="dialog-buttons">
          <v-btn
            text
            @click="displayUnpublishDialog = false"
          >{{ $store.state.user.langEng ? 'Cancel' : 'Avbryt' }}</v-btn>
          <v-btn
            color="red lighten-2"
            @click="unpublishArticle"
          >{{ $store.state.user.langEng ? 'Unpublish' : 'Avpublicera' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="displayDeleteArticleDialog" max-width="400px">
      <v-card>
        <v-card-title>{{ $store.state.user.langEng ? 'Are you sure?' : 'Är du säker?' }}</v-card-title>
        <v-card-text
          class="text-left"
        >{{ $store.state.user.langEng ? 'This article will be deleted and no longer searchable' : 'Denna artikel kommer raderas och ej längre dyka upp i sökresultat.' }}</v-card-text>
        <v-card-actions class="dialog-buttons">
          <v-btn
            text
            @click="displayDeleteArticleDialog = false"
          >{{ $store.state.user.langEng ? 'Cancel' : 'Avbryt' }}</v-btn>
          <v-btn
            color="red lighten-2"
            @click="deleteArticle"
          >{{ $store.state.user.langEng ? 'Delete' : 'Radera' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="displaySaveArticleDialog" max-width="400px">
      <v-card>
        <v-card-title>{{ $store.state.user.langEng ? 'Warning' : 'Varning' }}</v-card-title>
        <v-card-text
          class="text-left"
        >{{ $store.state.user.langEng ? 'This article is used by other classes as well' : 'Denna artikel används även av andra klasser.' }}</v-card-text>
        <v-card-actions class="dialog-buttons">
          <v-btn
            text
            @click="displaySaveArticleDialog = false"
          >{{ $store.state.user.langEng ? 'Cancel' : 'Avbryt' }}</v-btn>
          <v-btn color="primary" @click="saveArticle">{{ $store.state.user.langEng ? 'Save' : 'Spara' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Vue, Component, Watch } from "vue-property-decorator";
import RightPanel from "./RightPanel";
import { eventBus } from "@/main";
import { Rest } from "@/utils";

@Component({
  extends: RightPanel,
})
export default class CreateArticlePanel extends Vue {
  selectedCategories = [];
  klasses = [];
  filter = "";
  newCategory = "";
  toggleNewCategory = false;
  categoryError = false;
  displayUnpublishDialog = false;
  displayDeleteArticleDialog = false;
  displaySaveArticleDialog = false;

  @Watch("filter")
  watchFilter(val) {
    this.newCategory = val;
  }

  @Watch("$store.state.article.currentArticle.klasses", { deep: true })
  watchKlasses() {
    this.klasses = this.$store.state.article.currentArticle.klasses || [];
  }

  get article() {
    this.watchKlasses();
    return this.$store.state.article.currentArticle;
  }

  get isPublished() {
    const hasMyKlass = this.article.klasses.find(
      (k) => k.slug == this.$store.getters.classNameInUrl
    );
    return hasMyKlass && hasMyKlass.publishedAt;
  }

  emitSelectedCategories() {
    this.categoryError = false;
    let oldCategories = this.article.categories.slice();
    // console.log("oldCategories", oldCategories);
    let tempArray = [];
    // Add all selected categories (re-mapped)
    for (let i = 0; i < this.selectedCategories.length; i++) {
      // const name = this.selectedCategories[i].name;
      // tempArray.push(this.$store.state.article.categories[name]);
      tempArray.push(this.selectedCategories[i]);
    }
    // console.log("tempArray", tempArray);
    // detach unselected categories
    for (let oldCat of oldCategories) {
      const didRemoveOldCat = !tempArray.find((c) => c.name == oldCat.name);
      if (didRemoveOldCat) {
        tempArray.push({ ...oldCat, detach: true });
      }
    }
    this.article.categories = tempArray;
  }

  get categories() {
    let filter = new RegExp(this.filter, "i");
    let c = [];
    for (let i in this.$store.state.article.categories) {
      c.push(this.$store.state.article.categories[i]);
    }
    // Sort: isChecked, alphabetically
    return c
      .filter((c) => c.name.match(filter))
      .sort((a, b) => {
        const aIsChecked = !!this.selectedCategories.find(
          (sc) => sc.name == a.name
        );
        const bIsChecked = !!this.selectedCategories.find(
          (sc) => sc.name == b.name
        );
        return aIsChecked > bIsChecked
          ? -1
          : aIsChecked < bIsChecked
          ? 1
          : a.name < b.name
          ? -1
          : 1;
      });
  }

  hasCategoryError() {
    if (this.selectedCategories.filter((c) => !c.detach).length === 0) {
      this.categoryError = true;
      return true;
    } else {
      this.categoryError = false;
    }
  }

  get isDraft() {
    return this.article.deleted || !this.isPublished || !this.article._id;
  }

  saveArticle() {
    if (this.hasCategoryError()) {
      return;
    }
    const hasOtherKlasses = this.article.klasses.filter(
      (k) => k.slug != this.$store.getters.classNameInUrl
    ).length;

    if (hasOtherKlasses && !this.displaySaveArticleDialog) {
      this.displaySaveArticleDialog = true;
    } else {
      this.displaySaveArticleDialog = false;
      this.$store.commit(
        "setButtonAction",
        this.isDraft ? "saveDraft" : "save"
      );
    }
  }

  publishArticle() {
    if (this.hasCategoryError()) {
      return;
    }
    this.$store.commit("setButtonAction", "publish");
  }

  unpublishArticle() {
    this.$store.commit("setButtonAction", "unpublish");
    this.displayUnpublishDialog = false;
  }

  deleteArticle() {
    this.$store.commit("setButtonAction", "delete");
    this.displayDeleteArticleDialog = false;
  }

  detachArticle() {
    this.$store.commit("setButtonAction", "detach");
  }

  klassColor(/* i */) {
   /*  if (this.$store.state.klasses.hasOwnProperty(this.article.klasses[i].slug))
      return this.$store.state.klasses[this.article.klasses[i].slug].color;
    else */ return "black";
  }

  async addNewCategory() {
    this.newCategory = this.newCategory.trim();
    if (!this.newCategory) {
      return;
    }
    this.newCategory =
      this.newCategory[0].toUpperCase() + this.newCategory.substr(1);

    const response = await Rest.post("Category", { name: this.newCategory });

    // autoselect added category
    const categories = await Rest.get("Category", { sort: { name: "ASC" } });
    this.$store.commit("setCategories", categories);
    this.selectedCategories.unshift(response);
    this.emitSelectedCategories();

    this.newCategory = "";
    this.toggleNewCategory = false;
  }

  initCategories(values) {
    this.selectedCategories = [];
    if (Array.isArray(values)) {
      this.selectedCategories.push(...values);
    } else {
      this.selectedCategories.push(values);
    }
    this.emitSelectedCategories();
  }

  mounted() {
    eventBus.$on("init-edit-categories", () =>
      this.initCategories(this.article.categories)
    );
    eventBus.$on("clear-selected-categories", () => {
      this.selectedCategories = [];
    });
  }

  beforeDestroy() {
    eventBus.$off("init-edit-categories");
    eventBus.$off("clear-selected-categories");
  }
}
</script>

<style lang="scss">
#categories {
  // padding-left: 2px;
  .publish-buttons {
    padding: 0 15px;
    .mobile & {
      margin-top: 30px;
      justify-content: center;
    }
    .publish-btn,
    .draft-btn {
      margin: 0;
    }
    .draft-btn {
      min-width: 40%;
    }
    .publish-btn {
      min-width: 40%;
    }
  }
  .v-text-field__details {
    margin: 0;
  }
  #filtered-categories {
    width: 100%;
    height: 50vh;
    @media screen and (max-width: 994px) {
      height: 44vh;
    }
    overflow-y: auto;
    overflow-x: hidden;
    word-wrap: break-word;
    // padding: 10px 10px 10px 2px;
    margin-top: 10px;
    .add-new-category-button {
      margin: 0;
      .v-btn__content {
        justify-content: flex-start;
      }
    }
    .new-category-input {
      padding-top: 0;
      padding-left: 0.5em;
      animation-duration: 250ms;
    }
    .v-input--selection-controls {
      margin: 0;
      padding: 0;
      animation-duration: 150ms;
      label {
        font-size: 0.9em;
        padding-right: 1em;
      }
      .v-input__slot {
        margin: 0;
      }
    }
  }
  .v-messages {
    display: none;
  }
}
.dialog-buttons {
  display: flex;
  justify-content: space-around;
}
.draft-btn, .publish-btn{
  min-width: 150px !important;
  text-align: left !important;
}
</style>
